/**
 * Regras de exibição da Central de vagas (SPEC 2.5a §2/§4). Funções puras:
 * o "agora" é sempre injetado, e datas/horas usam o fuso do plantão
 * (`timezone` do item, ex.: `-03:00`), nunca o do navegador.
 */
import { formatInTimeZone } from 'date-fns-tz'

import type {
  VacancyBucket,
  VacancyBucketCounts,
  VacancyCounter,
  VacancyItem,
  VacancySector,
  VacancySpecialty
} from '@/Network/Types/Responses/VacancyCenterResponses'
import { VACANCY_BUCKET_COLORS, VACANCY_GROUP_COLOR } from './vacancyCenterColors'
import type { VacancyGroupBy } from './vacancyCenterUrl'

const DEFAULT_TIMEZONE = '-03:00'
const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']
const MINUTE_MS = 60_000
const URGENT_MINUTES = 6 * 60
const NEXT6H_MS = URGENT_MINUTES * MINUTE_MS
export const VACANCY_BUCKET_ORDER: readonly VacancyBucket[] = [
  'next6h',
  'today',
  'tomorrow',
  'week'
]

export const BUCKET_TITLES: Record<VacancyBucket, string> = {
  next6h: 'Próximas 6h',
  today: 'Hoje',
  tomorrow: 'Amanhã',
  week: 'Semana'
}

export interface VacancyGroup {
  key: string
  title: string
  color: string
  hint: string
  items: VacancyItem[]
  open: number
  awaiting: number
}

/** `date-fns-tz` aceita `-0300`; o backend manda `-03:00`. */
const tzOf = (timezone: string | null | undefined): string =>
  (timezone || DEFAULT_TIMEZONE).replace(':', '')

const pad = (value: number): string => String(value).padStart(2, '0')

/** Dia local `yyyy-MM-dd` no fuso dado. */
const dayKeyOf = (date: Date | string, timezone: string): string =>
  formatInTimeZone(new Date(date), tzOf(timezone), 'yyyy-MM-dd')

/** `yyyy-MM-dd` + N dias (aritmética de calendário, sem fuso). */
const addDaysToKey = (dayKey: string, days: number): string => {
  const [y, m, d] = dayKey.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d + days))
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

/** `'2026-09-18'` → `'sex 18/09'`. */
const dayLabel = (dayKey: string): string => {
  const [y, m, d] = dayKey.split('-').map(Number)
  const weekday = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
  return `${weekday} ${pad(d)}/${pad(m)}`
}

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

const byStart = (a: VacancyItem, b: VacancyItem): number =>
  new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime() || a.stationId - b.stationId

const countStates = (items: VacancyItem[]): { open: number; awaiting: number } => ({
  open: items.filter((item) => item.state === 'open').length,
  awaiting: items.filter((item) => item.state === 'awaiting').length
})

/** `'19h'`, `'07h30'` — minutos só quando ≠ 0. */
export function formatHourLabel(iso: string, timezone: string): string {
  const hour = formatInTimeZone(new Date(iso), tzOf(timezone), 'HH')
  const minutes = formatInTimeZone(new Date(iso), tzOf(timezone), 'mm')
  return minutes === '00' ? `${hour}h` : `${hour}h${minutes}`
}

/** "começou 13h" / "em 40 min" / "em 3h20" / "em 2 dias"; urgente abaixo de 6 h. */
export function formatCountdown(
  startsAt: string,
  timezone: string,
  now: Date
): { text: string; urgent: boolean } {
  const diffMs = new Date(startsAt).getTime() - now.getTime()
  // Decide pelo sinal da diferença, não pelo arredondamento: `Math.round` de
  // −0,4 dá `-0` (e `-0 < 0` é falso), o que mostraria "em 0 min".
  if (diffMs <= 0) return { text: `começou ${formatHourLabel(startsAt, timezone)}`, urgent: true }
  // Faltando menos de 30 s, "em 1 min" (nunca "em 0 min").
  const minutes = Math.max(1, Math.round(diffMs / MINUTE_MS))
  const urgent = minutes < URGENT_MINUTES
  if (minutes < 60) return { text: `em ${minutes} min`, urgent }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    const rest = minutes % 60
    return { text: `em ${hours}h${rest ? pad(rest) : ''}`, urgent }
  }
  const days = Math.round(hours / 24)
  return { text: `em ${days} ${days === 1 ? 'dia' : 'dias'}`, urgent }
}

/** "Hoje, qui 17/09 · 19h–07h" / "Amanhã, sex 18/09 · …" / "Sáb 19/09 · …". */
export function formatWhen(
  item: Pick<VacancyItem, 'startsAt' | 'endsAt' | 'timezone'>,
  now: Date
): string {
  const startKey = dayKeyOf(item.startsAt, item.timezone)
  const todayKey = dayKeyOf(now, item.timezone)
  const label = dayLabel(startKey)
  let day = capitalize(label)
  if (startKey === todayKey) day = `Hoje, ${label}`
  else if (startKey === addDaysToKey(todayKey, 1)) day = `Amanhã, ${label}`
  const hours = `${formatHourLabel(item.startsAt, item.timezone)}–${formatHourLabel(
    item.endsAt,
    item.timezone
  )}`
  return `${day} · ${hours}`
}

/**
 * `pendingSince` é o DIA da alocação (`insert_doctor_date` é `date`, sem
 * hora): "pendente desde 16/09". Sem data, não mostra nada.
 */
export function formatPendingSince(pendingSince: string | null): string | null {
  const match = pendingSince?.match(/^\d{4}-(\d{2})-(\d{2})/)
  return match ? `pendente desde ${match[2]}/${match[1]}` : null
}

/** "✓ Cobrado às 14:21", no fuso do plantão. */
export function formatReminderDone(lastReminderAt: string, timezone: string): string {
  return `✓ Cobrado às ${formatInTimeZone(new Date(lastReminderAt), tzOf(timezone), 'HH:mm')}`
}

/**
 * Subtítulo da coluna/grupo de urgência:
 * - Próximas 6h: "até 20h20 de hoje" / "até 04h de amanhã";
 * - Hoje: "depois das 20h20" (vazio se agora + 6h já é amanhã);
 * - Amanhã: "sex 18/09";
 * - Semana: "sáb 19/09 → qui 24/09" (fim = dia local de `window.end − 1 ms`).
 */
export function bucketHint(
  bucket: VacancyBucket,
  window: { start: string; end: string },
  now: Date,
  timezone: string = DEFAULT_TIMEZONE
): string {
  const todayKey = dayKeyOf(now, timezone)
  const limit = new Date(now.getTime() + NEXT6H_MS)
  const limitIsToday = dayKeyOf(limit, timezone) === todayKey
  const limitLabel = formatHourLabel(limit.toISOString(), timezone)

  if (bucket === 'next6h') return `até ${limitLabel} de ${limitIsToday ? 'hoje' : 'amanhã'}`
  if (bucket === 'today') return limitIsToday ? `depois das ${limitLabel}` : ''
  if (bucket === 'tomorrow') return dayLabel(addDaysToKey(todayKey, 1))

  const startKey = addDaysToKey(todayKey, 2)
  const endKey = dayKeyOf(new Date(new Date(window.end).getTime() - 1), timezone)
  if (endKey < startKey) return ''
  if (endKey === startKey) return dayLabel(startKey)
  return `${dayLabel(startKey)} → ${dayLabel(endKey)}`
}

/** "1 vaga" / "0 vagas" / "2 vagas". */
export function vacancyCountLabel(open: number): string {
  return `${open} ${open === 1 ? 'vaga' : 'vagas'}`
}

/**
 * "2 vagas · 3 aguardando", sem a parte zerada ("1 vaga" / "3 aguardando");
 * vazio quando as duas zeram (a coluna já diz "Nada nesta faixa").
 */
export function mixLabel(open: number, awaiting: number): string {
  return [open ? vacancyCountLabel(open) : '', awaiting ? `${awaiting} aguardando` : '']
    .filter(Boolean)
    .join(' · ')
}

const withArchived = (name: string, archived: boolean | undefined): string =>
  archived ? `${name} (arquivado)` : name

export function sectorLabel(sector: VacancySector | null): string {
  return sector ? withArchived(sector.name, sector.archived) : 'Sem setor'
}

export function specialtyLabel(specialty: VacancySpecialty | null): string {
  return specialty ? withArchived(specialty.name, specialty.archived) : 'Sem especialidade'
}

const NONE_COUNTER_LABELS: Record<'sector' | 'specialty' | 'unit', string> = {
  sector: 'Sem setor',
  specialty: 'Sem especialidade',
  unit: 'Sem unidade'
}

/** Nome do contador (chips e trilho), com "(arquivado)" e o nome do "sem" por tipo. */
export function counterLabel(
  counter: VacancyCounter,
  kind: 'sector' | 'specialty' | 'unit'
): string {
  if (counter.key === 'none') return counter.name || NONE_COUNTER_LABELS[kind]
  return withArchived(counter.name, counter.archived)
}

/** "até dd/mm" da janela: dia local de `end − 1 ms` (14h19 do 7º dia ainda está dentro). */
export function windowEndLabel(windowEnd: string, timezone: string = DEFAULT_TIMEZONE): string {
  return formatInTimeZone(new Date(new Date(windowEnd).getTime() - 1), tzOf(timezone), 'dd/MM')
}

/** "Atualizado agora" / "Atualizado há 12s" / "Atualizado há 3 min" / "Falha ao atualizar". */
export function updatedAgoLabel(
  lastLoadedAt: number | null,
  nowMs: number,
  refreshFailed: boolean
): string {
  if (refreshFailed) return 'Falha ao atualizar'
  if (lastLoadedAt === null) return ''
  const seconds = Math.max(0, Math.floor((nowMs - lastLoadedAt) / 1000))
  if (seconds < 5) return 'Atualizado agora'
  if (seconds < 60) return `Atualizado há ${seconds}s`
  return `Atualizado há ${Math.floor(seconds / 60)} min`
}

export function emptyListText(days: number): string {
  return `Nenhuma vaga aberta ou aguardando confirmação nos próximos ${days} dias`
}

export function truncatedText(shown: number, total: number): string {
  return `Mostrando ${shown} de ${total}. Use os filtros para ver o restante`
}

function groupByUrgency(
  items: VacancyItem[],
  now: Date,
  buckets: VacancyBucketCounts | undefined,
  window: { start: string; end: string } | undefined
): VacancyGroup[] {
  const timezone = items[0]?.timezone ?? DEFAULT_TIMEZONE
  return VACANCY_BUCKET_ORDER.map((bucket) => {
    const bucketItems = items.filter((item) => item.bucket === bucket).sort(byStart)
    // Com o corte de 400, a contagem certa é a do backend (`buckets`), não a dos itens.
    const counts = buckets?.[bucket] ?? countStates(bucketItems)
    const needsWindow = bucket === 'week' && !window
    return {
      key: bucket,
      title: BUCKET_TITLES[bucket],
      color: VACANCY_BUCKET_COLORS[bucket],
      hint: needsWindow ? '' : bucketHint(bucket, window ?? { start: '', end: '' }, now, timezone),
      items: bucketItems,
      open: counts.open,
      awaiting: counts.awaiting
    }
  })
}

function groupByKey(
  items: VacancyItem[],
  now: Date,
  identify: (item: VacancyItem) => { key: string; title: string }
): VacancyGroup[] {
  const groups = new Map<string, { title: string; items: VacancyItem[] }>()
  ;[...items].sort(byStart).forEach((item) => {
    const { key, title } = identify(item)
    const group = groups.get(key) ?? { title, items: [] }
    group.items.push(item)
    groups.set(key, group)
  })
  // Itens já ordenados: a ordem de inserção do Map = grupo com o item mais urgente primeiro.
  return [...groups.entries()].map(([key, group]) => {
    const first = group.items[0]
    return {
      key,
      title: group.title,
      color: VACANCY_GROUP_COLOR,
      hint: `mais urgente: ${formatCountdown(first.startsAt, first.timezone, now).text}`,
      items: group.items,
      ...countStates(group.items)
    }
  })
}

/**
 * Grupos da lista/kanban. Urgência: sempre as 4 faixas, na ordem. Setor e
 * unidade: um grupo por chave, ordenados pelo item mais urgente.
 */
export function groupVacancyItems(
  items: VacancyItem[],
  groupBy: VacancyGroupBy,
  now: Date,
  buckets?: VacancyBucketCounts,
  window?: { start: string; end: string }
): VacancyGroup[] {
  if (groupBy === 'sector') {
    return groupByKey(items, now, (item) => ({
      key: item.sector ? `sector:${item.sector.id}` : 'none',
      title: sectorLabel(item.sector)
    }))
  }
  if (groupBy === 'unit') {
    return groupByKey(items, now, (item) => ({
      key: `unit:${item.healthUnitId}`,
      title: item.healthUnitName
    }))
  }
  return groupByUrgency(items, now, buckets, window)
}

/**
 * "Abrir no Mês": Dashboard na lente Mês ancorada no dia do plantão, com a
 * unidade, o setor e a especialidade dele e o `stationId` do deep link.
 * `startDate` = início do dia LOCAL do navegador do dia do plantão (no fuso do
 * item) — é o que a Dashboard espera como âncora.
 */
export function buildOpenInMonthRoute(item: VacancyItem): {
  path: string
  query: Record<string, string>
} {
  const dayKey = dayKeyOf(item.startsAt, item.timezone)
  const query: Record<string, string> = {
    periodo: 'mes',
    startDate: new Date(`${dayKey}T00:00:00`).toISOString(),
    healthUnits: String(item.healthUnitId)
  }
  if (item.sector) query.sectorIds = String(item.sector.id)
  else query.withoutSector = 'true'

  if (!item.specialty) query.withoutSpecialty = 'true'
  else if (item.specialty.source === 'official') query.specialtyIds = String(item.specialty.id)
  else query.companySpecialtyIds = String(item.specialty.id)

  query.stationId = String(item.stationId)
  return { path: '/dashboard', query }
}

/** Toast do deep link `?vaga=` quando a vaga não está mais nos itens. */
export const VACANCY_DEEP_LINK_GONE = 'Vaga não está mais na janela'

/** Abertura padrão das abas do celular: a primeira faixa com itens (SPEC 2.5a.1 §4). */
export function firstBucketWithItems(buckets: VacancyBucketCounts | undefined): VacancyBucket {
  if (!buckets) return 'next6h'
  return (
    VACANCY_BUCKET_ORDER.find((bucket) => buckets[bucket].open + buckets[bucket].awaiting > 0) ??
    'next6h'
  )
}

/**
 * Subtítulo do cabeçalho (SPEC 2.5a.1 §2): "<empresa> · próximos N dias, até
 * dd/mm"; para 1 dia, "próximas 24h, até dd/mm HHh" (a janela é agora + 24h,
 * então o fim tem hora).
 */
export function windowSubtitle(
  companyName: string | null,
  days: number,
  windowEnd: string | null,
  timezone: string = DEFAULT_TIMEZONE
): string {
  let text = days === 1 ? 'próximas 24h' : `próximos ${days} dias`
  if (windowEnd) {
    text +=
      days === 1
        ? `, até ${formatInTimeZone(new Date(windowEnd), tzOf(timezone), 'dd/MM')} ${formatHourLabel(windowEnd, timezone)}`
        : `, até ${windowEndLabel(windowEnd, timezone)}`
  }
  return companyName ? `${companyName} · ${text}` : capitalize(text)
}

/** Indicador curto do celular: "agora" / "12s" / "3 min" / "Falha ao atualizar". */
export function updatedShortLabel(
  lastLoadedAt: number | null,
  nowMs: number,
  refreshFailed: boolean
): string {
  if (refreshFailed) return 'Falha ao atualizar'
  if (lastLoadedAt === null) return ''
  const seconds = Math.max(0, Math.floor((nowMs - lastLoadedAt) / 1000))
  if (seconds < 5) return 'agora'
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)} min`
}
